import { Button } from './ui/button';
import { BellRing } from 'lucide-react';

// Official WhatsApp glyph (lucide has no brand icons).
export const WhatsAppIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const toWhatsAppUrl = (msg, phone) => {
  const digits = (phone || '').replace(/[^\d]/g, '');
  // api.whatsapp.com directly: the wa.me redirect mangles emojis (astral
  // unicode) into replacement chars.
  const base = digits
    ? `https://api.whatsapp.com/send?phone=${digits}&`
    : 'https://api.whatsapp.com/send?';
  return `${base}text=${encodeURIComponent(msg)}`;
};

export const buildWhatsAppInviteUrl = ({ name, inviteToken, recipientName, phone }) => {
  // The /api/invite/{token} landing serves Open Graph tags (protagonist
  // photo card) for the WhatsApp preview, then redirects to the form.
  const link = `${window.location.origin}/api/invite/${inviteToken}`;
  const msg =
    `¡Hola ${name}! \uD83D\uDC4B\n\n` +
    `Estoy preparando un libro de recuerdos muy especial para ${recipientName} ` +
    `y me encantaría que participaras con tus recuerdos y anécdotas.\n\n` +
    `Puedes hacerlo desde este enlace:\n${link}\n\n` +
    `¡Mil gracias! \uD83D\uDC9B`;
  return toWhatsAppUrl(msg, phone);
};

export const buildWhatsAppReminderUrl = ({ name, inviteToken, recipientName, phone }) => {
  const link = `${window.location.origin}/api/invite/${inviteToken}`;
  const msg =
    `¡Hola ${name}! \uD83D\uDC4B\n\n` +
    `Te escribí hace unos días sobre el libro de recuerdos que estamos preparando para ${recipientName}.\n\n` +
    `Tus recuerdos y anécdotas son muy importantes para que el libro quede completo. ` +
    `Aún estás a tiempo de aportarlos aquí (solo te llevará unos minutos):\n${link}\n\n` +
    `¡Mil gracias! \uD83D\uDC9B`;
  return toWhatsAppUrl(msg, phone);
};

// Opens the promoter's own WhatsApp (click-to-chat) with the invite message
// pre-filled — the message is sent from their personal number.
export const WhatsAppInviteButton = ({ name, inviteToken, recipientName, phone, showLabel = true, testId }) => (
  <Button
    variant="outline"
    size="sm"
    className="gap-2 text-[#128C4B] border-[#25D366]/50 hover:bg-[#25D366]/10 hover:text-[#0d6e3a]"
    title="Enviar invitación por WhatsApp"
    onClick={() => window.open(buildWhatsAppInviteUrl({ name, inviteToken, recipientName, phone }), '_blank', 'noopener')}
    data-testid={testId || 'whatsapp-invite-btn'}
  >
    <WhatsAppIcon className="w-4 h-4" />
    {showLabel && <span className="hidden sm:inline">WhatsApp</span>}
  </Button>
);

// Softer follow-up message for contributors who haven't submitted yet.
export const WhatsAppReminderButton = ({ name, inviteToken, recipientName, phone, showLabel = true, testId, onSent }) => (
  <Button
    variant="outline"
    size="sm"
    className="gap-2 text-amber-700 border-amber-400/60 hover:bg-amber-50 hover:text-amber-800"
    title="Enviar recordatorio por WhatsApp"
    onClick={() => {
      window.open(buildWhatsAppReminderUrl({ name, inviteToken, recipientName, phone }), '_blank', 'noopener');
      if (onSent) onSent();
    }}
    data-testid={testId || 'whatsapp-reminder-btn'}
  >
    <BellRing className="w-4 h-4" />
    {showLabel && <span className="hidden sm:inline">Recordar</span>}
  </Button>
);
